<template>
  <transition name="leo-nl-pack-fade">
    <div
      v-if="visible"
      class="leo-nl-pack-banner"
      :class="{ 'is-ready': phase === 'ready', 'is-progress': phase === 'progress' }"
    >
      <div class="leo-nl-pack-orb" :class="'phase-' + phase">
        <span class="leo-nl-pack-locale">{{ localeLabel }}</span>
      </div>
      <div class="leo-nl-pack-body">
        <p class="leo-nl-pack-title">{{ titleText }}</p>
        <p v-if="subText" class="leo-nl-pack-sub">{{ subText }}</p>
        <el-progress
          v-if="phase === 'progress'"
          :percentage="progressPct"
          :stroke-width="4"
          :show-text="false"
          status="success"
        />
      </div>
      <div v-if="phase === 'prompt'" class="leo-nl-pack-actions">
        <el-button type="primary" size="mini" :loading="busy" @click="onEnable">Enable Leo NL</el-button>
        <el-button size="mini" @click="onLater">Later</el-button>
      </div>
      <button v-else type="button" class="leo-nl-pack-close" aria-label="Close" @click="dismiss">×</button>
    </div>
  </transition>
</template>

<script>
import {
  ensureNlLocalePack,
  dismissProvisionPrompt,
  frontendLangToLeoLocale,
  isBuiltinNlLocale,
  getNlLocalePackStatus,
  shouldPromptProvision
} from '@/utils/leoNlLocalePack'

export default {
  name: 'LeoNlLocalePackBanner',
  data () {
    return {
      visible: false,
      phase: 'hidden',
      locale: 'en',
      busy: false,
      progressPct: 0,
      subText: ''
    }
  },
  computed: {
    localeLabel () {
      const m = { en: 'EN', 'zh-Hant': '中', ja: 'JA', th: 'TH' }
      return m[this.locale] || String(this.locale || '').slice(0, 2).toUpperCase()
    },
    titleText () {
      if (this.phase === 'ready') return 'Leo NL language pack is ready'
      if (this.phase === 'progress') return 'Preparing Leo NL language understanding…'
      return 'Enable Leo NL for this language?'
    }
  },
  methods: {
    async checkLocale (lang) {
      const locale = frontendLangToLeoLocale(lang)
      this.locale = locale
      if (isBuiltinNlLocale(locale)) {
        this.visible = false
        this.$root.$emit('leo-nl-locale-changed', locale)
        return
      }
      try {
        const st = await getNlLocalePackStatus(lang)
        if (st.ready) {
          this.phase = 'ready'
          this.visible = true
          this.$root.$emit('leo-nl-locale-changed', locale)
          setTimeout(() => { this.visible = false }, 2500)
          return
        }
        if (st.inProgress && st.runUid) {
          this.phase = 'progress'
          this.visible = true
          await ensureNlLocalePack(lang, {
            onProgress: (row) => {
              const s = row.resultSummary || {}
              this.progressPct = Math.min(99, Math.round(Number(s.coveragePct || 50)))
            }
          })
          this.phase = 'ready'
          this.$root.$emit('leo-nl-locale-changed', locale)
          setTimeout(() => { this.visible = false }, 2500)
          return
        }
        if (shouldPromptProvision(st)) {
          this.phase = 'prompt'
          this.subText = st.estimatedUsd != null
            ? `≈ $${Number(st.estimatedUsd).toFixed(2)} estimated`
            : ''
          this.visible = true
        }
      } catch (e) {
        this.visible = false
      }
    },
    async onEnable () {
      this.busy = true
      this.phase = 'progress'
      this.progressPct = 8
      try {
        await ensureNlLocalePack(this.locale === 'zh-Hant' ? 'tw' : this.locale, {
          onProgress: (row) => {
            const s = row.resultSummary || {}
            this.progressPct = Math.min(99, Math.round(Number(s.coveragePct || this.progressPct + 5)))
          }
        })
        this.phase = 'ready'
        this.$root.$emit('leo-nl-locale-changed', this.locale)
        setTimeout(() => { this.visible = false }, 2500)
      } catch (e) {
        this.subText = e.message || 'failed'
        this.phase = 'prompt'
      } finally {
        this.busy = false
      }
    },
    onLater () {
      dismissProvisionPrompt(this.locale)
      this.dismiss()
    },
    dismiss () {
      this.visible = false
      this.phase = 'hidden'
    }
  },
  mounted () {
    this.$root.$on('leo-nl-locale-check', this.checkLocale)
  },
  beforeDestroy () {
    this.$root.$off('leo-nl-locale-check', this.checkLocale)
  }
}
</script>

<style scoped>
.leo-nl-pack-banner {
  position: fixed;
  top: 52px;
  right: 16px;
  z-index: 4000;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 420px;
  padding: 10px 14px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(12, 24, 48, 0.94), rgba(20, 40, 72, 0.92));
  border: 1px solid rgba(100, 180, 255, 0.35);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
  color: #e8f0ff;
}
.leo-nl-pack-orb {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle at 30% 30%, #7dd3fc, #2563eb 55%, #1e1b4b);
  box-shadow: 0 0 18px rgba(56, 189, 248, 0.45);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}
.leo-nl-pack-orb.phase-progress {
  animation: leo-nl-pulse 1.6s ease-in-out infinite;
}
.leo-nl-pack-body { flex: 1; min-width: 0; }
.leo-nl-pack-title { margin: 0; font-size: 13px; font-weight: 600; }
.leo-nl-pack-sub { margin: 4px 0 0; font-size: 11px; opacity: 0.75; }
.leo-nl-pack-actions { display: flex; gap: 6px; flex-shrink: 0; }
.leo-nl-pack-close {
  background: transparent;
  border: none;
  color: #94a3b8;
  font-size: 20px;
  cursor: pointer;
  line-height: 1;
  padding: 0 4px;
}
@keyframes leo-nl-pulse {
  0%, 100% { transform: scale(1); box-shadow: 0 0 12px rgba(56, 189, 248, 0.35); }
  50% { transform: scale(1.06); box-shadow: 0 0 22px rgba(56, 189, 248, 0.65); }
}
.leo-nl-pack-fade-enter-active, .leo-nl-pack-fade-leave-active { transition: opacity 0.25s, transform 0.25s; }
.leo-nl-pack-fade-enter, .leo-nl-pack-fade-leave-to { opacity: 0; transform: translateY(-8px); }
</style>



// WEBPACK FOOTER //
// src/components/LeoNlLocalePackBanner.vue